import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { Analysis } from '@/entities/analysis';
import {
  IQ_DATA_SOURCE_LABELS,
  IQ_DATA_SOURCE_ORDER,
  iqDataSourcesQueryOptions,
  type IqDataSource,
} from '@/entities/iq-data-source';
import { SlideOverPanel } from '@/shared/ui';
import { attachmentFor } from './attachment';
import { DataSourceList } from './DataSourceList';
import { DataSourceUpload } from './DataSourceUpload';

interface DataSourcesPanelProps {
  analysis: Analysis;
  open: boolean;
  onClose: () => void;
}

/**
 * The right-hand panel that attaches YouGov and Pathmatics files to one report.
 *
 * Two levels rather than a single scroll: the sources a workspace has activated are few, and
 * putting both upload surfaces on one screen would make it ambiguous which drop zone belongs
 * to which source.
 */
export const DataSourcesPanel = ({ analysis, open, onClose }: DataSourcesPanelProps) => {
  const { t } = useTranslation('analysis');
  const [selected, setSelected] = useState<IqDataSource | null>(null);
  const { data: connections } = useQuery(iqDataSourcesQueryOptions());

  const activated = useMemo(
    () =>
      IQ_DATA_SOURCE_ORDER.filter((source) =>
        (connections ?? []).some((item) => item.source === source && item.enabled),
      ),
    [connections],
  );

  const attachedSources = useMemo(
    () => new Set(activated.filter((source) => attachmentFor(analysis, source) !== null)),
    [activated, analysis],
  );

  // Reset on close, so a close-and-reopen lands back on the list rather than on whichever
  // source was last picked. The panel stays mounted to animate shut, so nothing else clears it.
  useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const title = selected
    ? t('dataSourcesPanel.uploadTitle', { name: IQ_DATA_SOURCE_LABELS[selected] })
    : t('dataSourcesPanel.title');

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      label={t('dataSourcesPanel.title')}
      closeLabel={t('dataSourcesPanel.close')}
      title={title}
      onBack={selected ? () => setSelected(null) : undefined}
      backLabel={t('dataSourcesPanel.back')}
      testId="data-sources-panel"
    >
      <div className="flex flex-col gap-6">
        {selected ? (
          <DataSourceUpload
            analysisId={analysis.id}
            source={selected}
            attachment={attachmentFor(analysis, selected)}
          />
        ) : (
          <DataSourceList
            sources={activated}
            attachedSources={attachedSources}
            onSelect={setSelected}
          />
        )}
      </div>
    </SlideOverPanel>
  );
};

DataSourcesPanel.displayName = 'DataSourcesPanel';
